import { createSelector } from '@ngrx/store';
import { getRoles } from 'src/app/shared/selectors/roles.selectors';
import { getDragonflyState } from '../reducers';

export const getEditFlowState = createSelector(getDragonflyState, (state) => state.editFlow);

export const getAllRoutes = createSelector(getEditFlowState, ({ allRoutes }) => allRoutes);

export const getIsCreate = createSelector(getEditFlowState, ({ isCreate }) => isCreate);

export const getIsEdit = createSelector(getEditFlowState, ({ isCreate }) => !isCreate);

export const getFlowName = createSelector(getEditFlowState, ({ flowName }) => flowName);

export const getContextValues = createSelector(getEditFlowState, ({ flowContextValues }) => flowContextValues);

export const getContext = createSelector(getEditFlowState, ({ flowContext }) => flowContext);

export const getActiveFlows = createSelector(getEditFlowState, ({ activeFlows }) => activeFlows);

export const getJsonData = createSelector(getEditFlowState, ({ jsonData }) => jsonData);

export const getJsonDataParsed = createSelector(getEditFlowState, ({ jsonData }) => {
  try {
    const jsonDataParsed = JSON.parse(jsonData);
    return jsonDataParsed;
  } catch (e) {
    return {};
  }
});

export const getJsonDataId = createSelector(getEditFlowState, ({ jsonData, sourceId }) => ({ jsonData, sourceId }));

export const getClearJsonData = createSelector(getEditFlowState, ({ resetJsonData, jsonData, sourceId }) => ({
  resetJsonData,
  jsonData,
  sourceId,
}));

export const getSubmit = createSelector(getEditFlowState, ({ submit }) => submit);

export const getValidate = createSelector(getEditFlowState, ({ validate }) => validate);

export const getInvalidJSON = createSelector(getEditFlowState, ({ validate }) => !validate);

export const getFlowNameValidation = createSelector(getFlowName, getSubmit, (flowName, submit) => !flowName && submit);

export const getJSONValid = createSelector(getJsonData, (jsonData) => {
  try {
    JSON.parse(jsonData);
    return true;
  } catch (e) {
    return false;
  }
});

export const getFlowContextValidation = createSelector(
  getContext,
  getSubmit,
  (flowContext, submit) => !flowContext && submit,
);

export const getFlowEqualityValidation = createSelector(
  getJSONValid,
  getJsonData,
  getFlowNameValidation,
  getFlowContextValidation,
  (jsonValid, jsonData, flowNameValidation, flowContextValidation) =>
    jsonValid
      ? JSON.parse(jsonData)?.flowID &&
        JSON.parse(jsonData)?.flowName &&
        JSON.parse(jsonData)?.flowID !== JSON.parse(jsonData)?.flowName &&
        !flowNameValidation &&
        !flowContextValidation
      : false,
);

export const getMissingStartValidation = createSelector(
  getJSONValid,
  getJsonData,
  getAllRoutes,
  getFlowNameValidation,
  getFlowContextValidation,
  (jsonValid, jsonData, allRoutes, flowNameValidation, flowContextValidation) =>
    jsonValid
      ? JSON.parse(jsonData)?.tasks?.length > 0 &&
        !allRoutes.includes(JSON.parse(jsonData)?.tasks?.[0]?.taskID) &&
        !flowNameValidation &&
        !flowContextValidation
      : false,
);

export const getFlowNamesValidation = createSelector(
  getJSONValid,
  getJsonData,
  getFlowNameValidation,
  getFlowName,
  (jsonValid, jsonData, flowNameValidation, flowName) =>
    !!(jsonValid ? JSON.parse(jsonData)?.flowName !== flowName && !flowNameValidation && flowName : false),
);

export const getAllValidations = createSelector(
  getJSONValid,
  getFlowEqualityValidation,
  getMissingStartValidation,
  getFlowNameValidation,
  getFlowContextValidation,
  getFlowNamesValidation,
  (
    jsonValid,
    flowEqualityValidation,
    missingStartValidation,
    flowNameValidation,
    flowContextValidation,
    flowNamesValidation,
  ) =>
    !(
      !jsonValid ||
      flowEqualityValidation ||
      missingStartValidation ||
      flowNameValidation ||
      flowContextValidation ||
      flowNamesValidation
    ),
);

export const getCreateNewRole = createSelector(getRoles, (roles) => !!roles?.['flow.action.create']);

export const getUpdateRole = createSelector(getRoles, (roles) => !!roles?.['flow.action.update']);

export const getUpdateAdvancedRole = createSelector(getRoles, (roles) => !!roles?.['flow.action.updateadvanced']);

export const getCanViewJSONRoles = createSelector(
  getCreateNewRole,
  getUpdateRole,
  getUpdateAdvancedRole,
  (createNewRole, updateRole, updateAdvancedRole) => createNewRole || (updateRole && updateAdvancedRole),
);

export const getCanViewJSONOnly = createSelector(
  getUpdateRole,
  getUpdateAdvancedRole,
  (updateRole, updateAdvancedRole) => !updateRole || !updateAdvancedRole,
);

export const getLanguages = createSelector(getEditFlowState, ({ languages: countries }) => countries);
